import type { ShapeTextRow } from '@/lib/shapes/shape-text'

/** Shown in place of real text while a shape's text is empty and being edited. */
const EMPTY_SHAPE_TEXT_PLACEHOLDER = 'Type here...'

export type PlaceholderChunk = { text: string; x: number; yTop: number }

/**
 * Lays the placeholder into `rows` — the SAME `flow.rows` real text and the editing caret both
 * use, so the placeholder starts on the exact row the caret already sits on (row 0's first slot is
 * where both `layoutShapeTextContent`'s initial caret and this packer's first character land).
 *
 * Deliberately greedy per CHARACTER rather than calling the shared `layoutShapeTextContent` word
 * engine: that engine defers an entire word to a later, wider row rather than splitting it — correct
 * for real body text, but it means a multi-character hint string like "Type here..." skips straight
 * past a narrow apex row (which can hold a single glyph but not the whole word "Type") looking for
 * a row wide enough for all four letters, reading as a dead gap above the placeholder. Packing one
 * character at a time lets that first, narrowest row hold whatever fits — even just "T" — with the
 * remainder continuing on the next row.
 *
 * Left-aligned at each row's own `startX` — the same x the caret sits at (`nextSlot`'s first slot
 * is `rows[0].segments[0].startX`) — rather than centered, so the placeholder always starts flush
 * with where typing would actually begin instead of floating in the middle of the row.
 */
function packPlaceholderChars(
  text: string,
  rows: ShapeTextRow[],
  measureTextPt: (value: string) => number
): PlaceholderChunk[] {
  const chunks: PlaceholderChunk[] = []
  let index = 0

  for (const row of rows) {
    if (index >= text.length) break
    const seg = row.segments[0]

    if (!seg) continue
    const available = seg.endX - seg.startX
    let end = index

    // Grow the run one character at a time while it still fits; if even the very next single
    // character doesn't fit, place it anyway as a last resort (mirrors the shared engine's own
    // "nothing fits — draw it and let the shape's own clip path cut it" rule) so a row can never
    // get stuck holding zero characters forever.
    while (end < text.length) {
      const candidateWidth = measureTextPt(text.slice(index, end + 1))

      if (candidateWidth > available && end > index) break
      end++
      if (candidateWidth > available) break
    }

    chunks.push({ text: text.slice(index, end), x: seg.startX, yTop: row.yTop })
    index = end
  }

  return chunks
}

/**
 * The placeholder chunks for an empty frame: `packPlaceholderChars` over the frame's rows, falling
 * back to its single `fallbackRow` when the rows hold nothing packable.
 */
export function layoutPlaceholderChunks(
  rows: ShapeTextRow[],
  fallbackRow: ShapeTextRow | undefined,
  measureTextPt: (value: string) => number
): PlaceholderChunk[] {
  const chunks = packPlaceholderChars(EMPTY_SHAPE_TEXT_PLACEHOLDER, rows, measureTextPt)

  // Zero chunks means zero packable rows — and an empty text frame that paints no placeholder
  // paints NOTHING: its fill and stroke are transparent, so once it is deselected nothing marks
  // where it is and the frame reads as deleted (WMPCM-9763). Two ways to
  // land here: a frame too short for one line band (see `fallbackRow`'s own doc — the common
  // case for a box dragged near the minimum size), or a row list whose segments were entirely
  // consumed by wrap bands. `fallbackRow` is one row at the same origin as the fallback
  // caret, and `packPlaceholderChars` always places at least one character on a row it is
  // handed, so this cannot come back empty a second time.
  if (chunks.length > 0 || !fallbackRow) return chunks
  return packPlaceholderChars(EMPTY_SHAPE_TEXT_PLACEHOLDER, [fallbackRow], measureTextPt)
}
